import React, { useState, useMemo, ReactElement, useEffect } from 'react';

import classNames from 'classnames';

import { setFavorite } from '@/api/customer';
import useAccount from '@/store/account';
import useThrottle from '@/hooks/throttle';

import styles from './favorite.module.scss';
import { WeeeIcon } from '@/components/common/Icon';
import useNeedLogin from '@/hooks/useNeedLogin';
import { appCollectStatusNotify } from '@/utils/weeeapp-notify';
import { twMerge } from 'tailwind-merge';

export interface HeartProps {
  className?: string;
  id?: number;
  /** targetCollected 点击收藏按钮之后收藏返回true，点击之后取消收藏返回false*/
  onClick?: (targetCollected: boolean) => void;
  isLogin?: boolean;
  color?: string;
  isFavorite?: boolean;
  onSuccess?: (
    id: number | string,
    isFavorite?: boolean,
    supportPurchaseStatusNotify?: boolean,
    next?: () => void
  ) => void;
  render?: (
    node?: boolean,
    toggleCollect?: (e?: React.MouseEvent<HTMLElement>) => void,
    dom?: React.ReactNode
  ) => React.ReactNode;
  isInCard?: boolean;
  showToastInPDP?: () => void;
  supportPurchaseStatusNotify?: boolean;
}

function Favorite({
  className,
  id,
  onSuccess,
  onClick,
  color,
  isFavorite,
  render,
  isInCard,
  supportPurchaseStatusNotify
}: HeartProps) {
  const [collected, setCollected] = useState<boolean>(isFavorite || false);
  const { checkIsLogin } = useNeedLogin();

  // const { favoriteIds } = useAccount();
  const toggleCollect = useThrottle(() => {
    onClick?.(!collected);
    if (onSuccess) {
      return onSuccess?.(id, !isFavorite, supportPurchaseStatusNotify, () => {
        setCollected(collected => !collected);
      });
    }
  }, 500);
  const handleClick = useThrottle(e => {
    e.stopPropagation();
    e.preventDefault();
    if (!checkIsLogin()) return;
    toggleCollect();
  }, 500);
  const defaultNode = (
    <button
      type="button"
      className={twMerge(
        'flex p-0 m-0 bg-transparent border-none outline-none appearance-none text-inherit',
        className
      )}
      style={{ display: 'flex' }}
      onClick={handleClick}>
      {!isInCard ? (
        <WeeeIcon
          type={collected ? 'iconfavorite' : 'iconfavorite_h'}
          className={classNames(styles.pdpFavoriteIcon, { [styles.collected]: collected })}
        />
      ) : collected ? (
        <img src={require('./images/bookmark2.png')} alt="bookmark image" />
      ) : (
        <img src={require('./images/bookmark1.png')} alt="bookmark image" />
      )}
    </button>
  );
  useEffect(() => {
    setCollected(isFavorite);
  }, [isFavorite]);
  if (render) {
    return render(collected, toggleCollect, defaultNode) as ReactElement<any>;
  }
  return defaultNode;
}

const FavoriteWrapper = ({ id, onClick, render, showToastInPDP, ...rest }: HeartProps) => {
  const { favoriteIds = [], syncFavoriteIds, isLogin } = useAccount();
  const isFavorite = useMemo(() => {
    return favoriteIds?.includes(+id);
  }, [id, favoriteIds?.join('_')]);
  const onSuccess = (id, collected, supportPurchaseStatusNotify = false, next) => {
    let newIds = [...favoriteIds];
    if (collected) {
      newIds = newIds.concat(id);
      showToastInPDP && showToastInPDP();
    } else {
      newIds = favoriteIds.filter(item => item !== +id);
    }
    supportPurchaseStatusNotify && appCollectStatusNotify([id], collected ? 'add' : 'remove');
    setFavorite({
      type: 'product_favorite_a',
      target_id: id
    })
      .then(res => {
        if (res?.result) {
          // trackByFavoriteAdd(res.object);
          next();

          syncFavoriteIds(newIds);
        } else {
          supportPurchaseStatusNotify && appCollectStatusNotify(favoriteIds, 'reset');
          syncFavoriteIds(favoriteIds);
        }
      })
      .catch(e => {
        supportPurchaseStatusNotify && appCollectStatusNotify(favoriteIds, 'reset');
        syncFavoriteIds(favoriteIds);
      });
  };

  return (
    <Favorite
      id={id}
      onClick={onClick}
      isFavorite={isFavorite}
      {...rest}
      onSuccess={onSuccess}
      render={(id, toggle, defaultNode) => {
        const node = render ? render(id, toggle, defaultNode) : defaultNode;
        return node;
      }}
    />
  );
};

export default FavoriteWrapper;
